'use client';

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { hydrate, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { PersistQueryClientProvider, removeOldestQuery } from '@tanstack/react-query-persist-client';
import { useEffect, useState } from 'react';
import { withNoSessionWorking } from '@/data/code-sessions/restored-session-lists';
import { handleSessionExpired, isSessionExpiredError } from '@/lib/auth/handle-session-expired';
import { MINUTE } from '@/lib/constants';
import { isDesktopTrayPath } from '@/lib/desktop-tray-window';
import { defaultQueryRetry } from '@/lib/query-retry';
import { STORAGE_KEYS } from '@/lib/storage-keys';
export function createQueryClientConfig() {
  return {
    queryCache: new QueryCache({
      onError: error => {
        if (isSessionExpiredError(error)) {
          handleSessionExpired();
        }
      }
    }),
    mutationCache: new MutationCache({
      onError: error => {
        if (isSessionExpiredError(error)) {
          handleSessionExpired();
        }
      }
    }),
    defaultOptions: {
      queries: {
        staleTime: MINUTE * 5,
        gcTime: Infinity,
        structuralSharing: false,
        placeholderData: (previousData: unknown) => previousData,
        retry: defaultQueryRetry
      }
    }
  };
}

// Reuse the browser client across navigation. Server renders always get their own client.
let queryClientSingleton: QueryClient | null = null;

// Each window drops its queries when another window removes this key (see QueryProvider).
// Removing a key that is already gone notifies no window, so write a placeholder first; the
// restore reads an empty value as no cache.
function removePersistedQueryCache() {
  try {
    localStorage.setItem(STORAGE_KEYS.QUERY_CACHE, '');
  } catch {
    // A full quota: the removal below then notifies other windows only if a cache was saved.
  }
  localStorage.removeItem(STORAGE_KEYS.QUERY_CACHE);
}
export async function clearQueryCache() {
  if (queryClientSingleton) {
    queryClientSingleton.clear();
  }
  if (typeof window !== 'undefined') {
    removePersistedQueryCache();
    localStorage.removeItem('installation-storage');
  }
}
export async function clearAllCaches() {
  console.log('[Cache] Clearing all client-side caches...');
  if (queryClientSingleton) {
    queryClientSingleton.clear();
  }
  if (typeof window !== 'undefined') {
    removePersistedQueryCache();
    // Clear known app cache keys (not localStorage.clear() to preserve auth/third-party state)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('mrge-') || key.startsWith('sidebar-') || key === 'installation-storage' || key === 'theme' || key === 'cubic_installation_flow')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    sessionStorage.clear();
    console.log('[Cache] Cleared app caches:', keysToRemove.length, 'localStorage keys + sessionStorage');
  }
}

// Keep track of which queries we've already processed for persistence
const alreadyPersistedQueries = new Set<string>();
// Keep track of when we last persisted the cache
let lastPersistTime = 0;

/**
 * Chrome caps an origin's localStorage at about 5M characters shared by every key. The
 * cache stays well under that so pending review comments and preferences still fit.
 */
export const QUERY_CACHE_MAX_CHARS = 2_000_000;

// Changing this discards every browser's persisted cache on its next load. Last changed
// when chat messages stopped being persisted, to drop the ones already stored.
export const QUERY_CACHE_BUSTER = 'without-chat-messages';

/** A saved cache records the user whose queries it holds. */
type OwnedPersistedClient = PersistedClient & {
  owner?: string;
};

// The signed-in user whose queries this window's cache holds. It is null when signed out and
// undefined until AuthProvider reports the first session. Only a signed-in window saves, so no
// save replaces the saved cache before it is restored or while signed out.
let cacheOwner: string | null | undefined;

/**
 * AuthProvider reports each session's user here before it renders that user. When the user
 * changes, this window drops the previous user's queries and restores the new user's saved cache,
 * so one account's queries never reach another account's session.
 */
export function setQueryCacheOwner(owner: string | null) {
  if (owner === cacheOwner) {
    return;
  }
  const previousOwner = cacheOwner;
  cacheOwner = owner;
  if (!queryClientSingleton) {
    return;
  }
  if (previousOwner !== undefined) {
    queryClientSingleton.clear();
  }
  restoreSavedQueryCache(queryClientSingleton, owner);
}
function restoreSavedQueryCache(client: QueryClient, owner: string | null) {
  try {
    const saved: OwnedPersistedClient | null = JSON.parse(localStorage.getItem(STORAGE_KEYS.QUERY_CACHE) || 'null');
    if (!saved || saved.owner !== owner || saved.buster !== QUERY_CACHE_BUSTER) {
      return;
    }
    console.log(`[QueryProvider] Restoring cache with ${saved.clientState.queries.length} queries`);
    // Saved data paints at once but counts as stale, so each query refetches when it is next
    // used, even one whose staleTime would otherwise keep an earlier session's data for good.
    for (const query of saved.clientState.queries) {
      query.state.isInvalidated = true;
    }
    // Older caches may contain paused mutations that have no defaults to resume them.
    hydrate(client, {
      ...saved.clientState,
      mutations: [],
      queries: saved.clientState.queries.map(withNoSessionWorking)
    });
  } catch (err) {
    // AuthProvider calls this before rendering the user, so a bad cache must not throw.
    console.error('[QueryProvider] Failed to restore the saved query cache:', err);
  }
}
export function createQueryCachePersister(storage: Storage | undefined) {
  const persister = createSyncStoragePersister({
    storage: storage && {
      getItem: key => storage.getItem(key),
      setItem: (key, value) => {
        if (value.length > QUERY_CACHE_MAX_CHARS) {
          throw new RangeError(`Query cache is ${value.length} chars, over its budget`);
        }
        storage.setItem(key, value);
      },
      removeItem: key => storage.removeItem(key)
    },
    key: STORAGE_KEYS.QUERY_CACHE,
    throttleTime: 5000,
    // Increase throttle time to 5 seconds
    // When a save throws (over budget above, or a real full quota), drop the query with
    // the oldest data and save again until the cache fits.
    retry: removeOldestQuery,
    serialize: data => {
      const now = Date.now();
      // Only log if it's been at least 5 seconds since last persist
      if (now - lastPersistTime > 5000) {
        console.log(`[QueryProvider] Serializing cache with ${data.clientState.queries.length} queries`);
        lastPersistTime = now;
      }
      return JSON.stringify(data);
    }
  });
  return {
    ...persister,
    persistClient: (client: PersistedClient) => {
      if (cacheOwner) {
        const owned: OwnedPersistedClient = {
          ...client,
          owner: cacheOwner
        };
        void persister.persistClient(owned);
      }
    },
    // setQueryCacheOwner restores the saved cache once it knows whose session this is.
    restoreClient: () => undefined
  };
}
export default function QueryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = useState(() => {
    if (typeof window === 'undefined') {
      return new QueryClient(createQueryClientConfig());
    }
    queryClientSingleton ??= new QueryClient(createQueryClientConfig());
    return queryClientSingleton;
  });
  const [persister] = useState(() => {
    try {
      // The desktop tray window restores the main window's saved cache but never saves over it.
      const savesCache = typeof window !== 'undefined' && !isDesktopTrayPath(window.location.pathname);
      return createQueryCachePersister(savesCache ? window.localStorage : undefined);
    } catch (err) {
      console.error('[QueryProvider] Failed to set up React Query persistence:', err);
      return createSyncStoragePersister({
        storage: undefined
      });
    }
  });
  useEffect(() => {
    // Every window of this origin except the desktop tray popover saves its whole cache to the
    // same key. When another window removes the key, as sign-out does, this window's next save
    // would write the previous session's queries back.
    const dropCacheClearedElsewhere = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.QUERY_CACHE && event.newValue === null) {
        queryClient.clear();
      }
    };
    window.addEventListener('storage', dropCacheClearedElsewhere);
    return () => window.removeEventListener('storage', dropCacheClearedElsewhere);
  }, [queryClient]);
  return <PersistQueryClientProvider client={queryClient} persistOptions={{
    persister,
    buster: QUERY_CACHE_BUSTER,
    dehydrateOptions: {
      // No mutation registers defaults, so a paused one restored here would stay pending.
      shouldDehydrateMutation: () => false,
      shouldDehydrateQuery: query => {
        const shouldPersist = query.meta?.persist === true && query.state.status === 'success';

        // Only log if we haven't seen this query key before
        if (shouldPersist) {
          const queryKeyStr = String(query.queryKey[0]);
          if (!alreadyPersistedQueries.has(queryKeyStr)) {
            console.log(`[QueryProvider] Persisting query for first time: ${queryKeyStr}`);
            alreadyPersistedQueries.add(queryKeyStr);
          }
        }
        return shouldPersist;
      }
    },
    maxAge: Infinity // Never expire persisted queries
  }}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>;
}